import type { FrameType, GalleryArtwork, GalleryStats } from './types';

interface TradeAssetDto {
  id: string;
  title?: string | null;
  price?: number | string | null;
  currency?: string | null;
  type?: string | null;
  description?: string | null;
  artistName?: string | null;
  artistAvatar?: string | null;
  coverImage?: string | null;
  media?: string | string[] | null;
}

interface TradeAssetsResponse {
  data?: {
    assets?: TradeAssetDto[];
  };
  success?: boolean;
}

interface ManifestArtwork {
  id?: string;
  title?: string;
  artistName?: string;
  artistAvatar?: string;
  description?: string;
  imageUrl?: string;
  url?: string;
  width?: number;
  height?: number;
  price?: number;
  currency?: string;
}

interface ManifestResponse {
  artworks?: ManifestArtwork[];
}

const API_URL =
  import.meta.env.VITE_ARTCHAIS_API_URL ||
  'https://api.artchais.com';
const MANIFEST_URL = import.meta.env.VITE_GALLERY_MANIFEST_URL || '';
const LEGACY_ASSET_BASE_URLS = [
  'https://artchaistorage-1367525306.cos.ap-beijing.myqcloud.com',
  'https://s3.cn-north-1.amazonaws.com.cn/artchais.com',
  'https://mentors.artchais.com',
  'https://source.artchais.com',
];
const ASSET_BASE_URL = import.meta.env.VITE_ASSET_BASE_URL || 'https://source.artchais.com';

const imageExtensions = ['.jpg', '.jpeg', '.png', '.webp', '.avif', '.gif'];

function isImageUrl(url: string): boolean {
  const clean = url.split('?')[0].split('#')[0].toLowerCase();
  return imageExtensions.some((ext) => clean.endsWith(ext));
}

function parseMediaList(media: string | string[] | null | undefined): string[] {
  if (Array.isArray(media)) return media.filter(Boolean);
  if (!media) return [];
  try {
    const parsed = JSON.parse(media) as unknown;
    if (Array.isArray(parsed)) return parsed.filter((item): item is string => typeof item === 'string' && Boolean(item));
  } catch {
    // Plain URL strings are still valid media values.
  }
  return media.trim() ? [media.trim()] : [];
}

function normalizeAssetUrl(url: string): string {
  return LEGACY_ASSET_BASE_URLS.reduce((next, legacyBase) => next.replace(legacyBase, ASSET_BASE_URL), url);
}

function withGalleryCdnCacheKey(url: string): string {
  if (!url.startsWith(ASSET_BASE_URL)) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}ac-gallery=1`;
}

function escapeSvgText(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function makeArtworkPosterDataUrl(title: string, artistName: string, type?: string | null): string {
  const safeTitle = escapeSvgText(title || 'Untitled Artwork');
  const safeArtist = escapeSvgText(artistName || 'ArtChais Artist');
  const safeType = escapeSvgText(type || 'archive work');
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="900" height="1200" viewBox="0 0 900 1200">
      <defs>
        <linearGradient id="bg" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0" stop-color="#d9c9ac"/>
          <stop offset="0.55" stop-color="#a9987b"/>
          <stop offset="1" stop-color="#5d5144"/>
        </linearGradient>
        <filter id="grain">
          <feTurbulence type="fractalNoise" baseFrequency="0.75" numOctaves="3" stitchTiles="stitch"/>
          <feColorMatrix type="saturate" values="0"/>
          <feComponentTransfer>
            <feFuncA type="table" tableValues="0 0.24"/>
          </feComponentTransfer>
          <feBlend mode="multiply" in2="SourceGraphic"/>
        </filter>
      </defs>
      <rect width="900" height="1200" fill="url(#bg)"/>
      <rect x="70" y="80" width="760" height="1040" rx="24" fill="rgba(255,255,255,0.13)" stroke="#2f271f" stroke-width="4"/>
      <circle cx="450" cy="420" r="190" fill="none" stroke="#2f271f" stroke-width="16" opacity="0.72"/>
      <path d="M260 650 C340 500 560 500 640 650" fill="none" stroke="#2f271f" stroke-width="18" stroke-linecap="round" opacity="0.76"/>
      <text x="450" y="820" text-anchor="middle" font-family="Inter, Arial, sans-serif" font-size="54" font-weight="800" fill="#211a14">${safeTitle}</text>
      <text x="450" y="895" text-anchor="middle" font-family="Inter, Arial, sans-serif" font-size="34" font-weight="600" fill="#44362a">${safeArtist}</text>
      <text x="450" y="970" text-anchor="middle" font-family="Inter, Arial, sans-serif" font-size="24" letter-spacing="6" fill="#f0ddaa">${safeType.toUpperCase()}</text>
      <rect width="900" height="1200" filter="url(#grain)" opacity="0.32"/>
    </svg>
  `.trim();
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

function extractDimensions(text: string): { width: number; height: number } | null {
  const match = text.match(/(\d{1,3}(?:\.\d+)?)\s*[x×*]\s*(\d{1,3}(?:\.\d+)?)/i);
  if (!match) return null;
  const width = Number(match[1]);
  const height = Number(match[2]);
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) return null;
  return { width, height };
}

function classifyFrame(input: {
  width: number;
  height: number;
  aspect: number;
  type?: string | null;
  text: string;
}): FrameType {
  const area = input.width * input.height;
  const text = `${input.type || ''} ${input.text}`.toLowerCase();
  if (area >= 7000 || input.width >= 120 || input.height >= 120) return 'monumental';
  if (text.includes('sculpture') || text.includes('ceramic') || text.includes('porcelain') || text.includes('耳饰')) {
    return 'shelf';
  }
  if (input.width <= 24 && input.height <= 24) return 'shelf';
  if (input.aspect >= 1.65 || input.aspect <= 0.62) return 'mural';
  return 'wall';
}

function chooseImageCandidate(candidates: string[]): string | null {
  const images = candidates
    .filter(isImageUrl)
    .map((url) => withGalleryCdnCacheKey(normalizeAssetUrl(url)));
  if (images.length === 0) return null;
  return images.sort((a, b) => {
    const score = (url: string) => {
      if (url.includes('.cos.') && url.includes('.myqcloud.com')) return 0;
      if (url.includes('artchaistorage-1367525306')) return 1;
      if (url.includes('s3.cn-north-1.amazonaws.com.cn')) return 2;
      return 3;
    };
    return score(a) - score(b);
  })[0];
}

function makeArtworkDedupeKey(artwork: GalleryArtwork): string {
  return `${artwork.artistName}::${artwork.title}`
    .normalize('NFKC')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

function dedupeGalleryArtworks(artworks: GalleryArtwork[]): GalleryArtwork[] {
  const seen = new Set<string>();
  return artworks.filter((artwork) => {
    const key = makeArtworkDedupeKey(artwork);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function fallbackArtworks(): GalleryArtwork[] {
  const samples = [
    {
      id: 'sample-forest',
      title: 'Whispers of the Forest',
      artistName: 'ArtChais Lab',
      description: 'A warm calibration piece used when live artworks are unavailable.',
      imageUrl: 'https://images.unsplash.com/photo-1448375240586-882707db888b?auto=format&fit=crop&w=1200&q=80',
      width: 120,
      height: 150,
    },
    {
      id: 'sample-spiral',
      title: 'Blue Aperture',
      artistName: 'ArtChais Lab',
      description: 'A shelf-scale study for small format lighting.',
      imageUrl: 'https://images.unsplash.com/photo-1541961017774-22349e4a1262?auto=format&fit=crop&w=1200&q=80',
      width: 40,
      height: 40,
    },
    {
      id: 'sample-horizon',
      title: 'Atrium Horizon',
      artistName: 'ArtChais Lab',
      description: 'A wide mural placeholder for wall grazing tests.',
      imageUrl: 'https://images.unsplash.com/photo-1579783900882-c0d3dad7b119?auto=format&fit=crop&w=1200&q=80',
      width: 160,
      height: 70,
    },
  ];

  return samples.map((item) => {
    const aspect = item.width / item.height;
    return {
      ...item,
      sourceUrl: item.imageUrl,
      aspect,
      frameType: classifyFrame({ ...item, aspect, text: item.description }),
    };
  });
}

async function normalizeArtwork(
  raw: ManifestArtwork | TradeAssetDto,
  source: 'manifest' | 'trade-assets',
): Promise<GalleryArtwork | null> {
  const mediaCandidates =
    source === 'manifest'
      ? [(raw as ManifestArtwork).imageUrl, (raw as ManifestArtwork).url].filter(Boolean)
      : [(raw as TradeAssetDto).coverImage, ...parseMediaList((raw as TradeAssetDto).media)].filter(Boolean);
  const description = raw.description || '';
  const explicitDimensions = source === 'manifest'
    ? {
        width: Number((raw as ManifestArtwork).width) || 0,
        height: Number((raw as ManifestArtwork).height) || 0,
      }
    : extractDimensions(`${raw.title || ''}\n${description}`) || { width: 0, height: 0 };
  const originalImageUrl = chooseImageCandidate(mediaCandidates as string[]);
  const title = raw.title?.trim() || 'Untitled Artwork';
  const artistName = raw.artistName?.trim() || 'ArtChais Artist';
  const imageUrl = originalImageUrl
    ? originalImageUrl
    : makeArtworkPosterDataUrl(title, artistName, (raw as TradeAssetDto).type);
  const id = raw.id || imageUrl;
  const rawArtistAvatar = source === 'manifest'
    ? (raw as ManifestArtwork).artistAvatar
    : (raw as TradeAssetDto).artistAvatar;
  const artistAvatar = rawArtistAvatar ? withGalleryCdnCacheKey(normalizeAssetUrl(rawArtistAvatar)) : undefined;
  const loadedDimensions = explicitDimensions.width && explicitDimensions.height
    ? explicitDimensions
    : originalImageUrl
      ? { width: 120, height: 90 }
      : { width: 90, height: 120 };
  const aspect = Math.max(0.25, Math.min(4, loadedDimensions.width / loadedDimensions.height || 1));
  const numericPrice = Number(raw.price);

  return {
    id,
    title,
    artistName,
    artistAvatar,
    description,
    imageUrl,
    sourceUrl: id.startsWith('post-') ? `https://artchais.com/trade/${encodeURIComponent(id)}` : imageUrl,
    price: Number.isFinite(numericPrice) ? numericPrice : undefined,
    currency: raw.currency || undefined,
    width: loadedDimensions.width,
    height: loadedDimensions.height,
    aspect,
    frameType: classifyFrame({
      width: loadedDimensions.width,
      height: loadedDimensions.height,
      aspect,
      type: (raw as TradeAssetDto).type,
      text: `${title}\n${description}`,
    }),
  };
}

async function loadManifest(): Promise<GalleryArtwork[] | null> {
  if (!MANIFEST_URL) return null;
  const response = await fetch(MANIFEST_URL, { cache: 'no-store' });
  if (!response.ok) throw new Error(`Manifest request failed: ${response.status}`);
  const json = (await response.json()) as ManifestResponse | ManifestArtwork[];
  const rawArtworks = Array.isArray(json) ? json : json.artworks || [];
  const normalized = await Promise.all(rawArtworks.map((item) => normalizeArtwork(item, 'manifest')));
  return dedupeGalleryArtworks(normalized.filter((item): item is GalleryArtwork => Boolean(item)));
}

async function loadTradeAssets(): Promise<GalleryArtwork[]> {
  const response = await fetch(`${API_URL}/api/trade/assets`, { cache: 'no-store' });
  if (!response.ok) throw new Error(`Trade assets request failed: ${response.status}`);
  const json = (await response.json()) as TradeAssetsResponse;
  const assets = json.data?.assets || [];
  const normalized = await Promise.all(assets.map((item) => normalizeArtwork(item, 'trade-assets')));
  return dedupeGalleryArtworks(normalized.filter((item): item is GalleryArtwork => Boolean(item)));
}

export async function loadGalleryArtworks(): Promise<{ artworks: GalleryArtwork[]; stats: GalleryStats }> {
  try {
    const manifest = await loadManifest();
    if (manifest && manifest.length > 0) {
      return { artworks: manifest, stats: { source: 'manifest', loaded: manifest.length, skipped: 0 } };
    }
  } catch (error) {
    console.warn('[gallery] Manifest unavailable, falling back to trade assets.', error);
  }

  try {
    const artworks = await loadTradeAssets();
    if (artworks.length > 0) {
      return { artworks, stats: { source: 'trade-assets', loaded: artworks.length, skipped: 0 } };
    }
  } catch (error) {
    console.warn('[gallery] Trade assets unavailable, using local fallback.', error);
  }

  const fallback = fallbackArtworks();
  return { artworks: fallback, stats: { source: 'fallback', loaded: fallback.length, skipped: 0 } };
}
